import { HomeOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Breadcrumb, Dropdown, Tag } from "antd";
import TitlePage from "./TitlePage";
import { useContext, useMemo } from "react";
import { MenuProps } from "antd/lib";
import { useClient } from "hooks/useClient";
import { generateUserLogoutEvent } from "api/generated";
import { logOut } from "security";
import { MenuItem } from "../menu/menuTypes";
import { findMenuItemByPath } from "./menu/menuUtils";
import DistributionPointSelected from "context/MyDistributionPoint";
import useLoggedUser from "hooks/useLoggedUser";

interface HeaderBarProps {
  pathname: string;
  userRole: string;
  userEmail: string;
  menu: MenuItem[];
}

function HeaderBar({ pathname, userRole, userEmail, menu }: HeaderBarProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { providerDataOrigin } = useLoggedUser();

  function onLogout() {
    generateUserLogoutEvent({ client }).finally(() => {
      logOut();
    });
  }

  const userProfile = useMemo(() => {
    switch (userRole) {
      case "ADMINISTRATOR":
        return "Administrador";
      case "SUPERVISOR":
        return "Supervisor";
      case "OPERATOR":
        return "Operador";
      default:
        return "Auditor fiscal";
    }
  }, [userRole]);

  const provider = useMemo(() => {
    switch (providerDataOrigin) {
      case "G4S":
        return { name: "G4S", color: "blue" };
      case "EXTERNAL":
        return { name: "Liquidaciones", color: "green" };
      case "UNKNOWN":
      default:
        return undefined;
    }
  }, [providerDataOrigin]);

  const userDropdown: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [
      {
        key: "profile",
        label: <span style={{ cursor: "default" }}>{userProfile}</span>,
      },
      {
        key: "email",
        label: <span style={{ cursor: "default" }}>{userEmail}</span>,
      },
    ];

    if (provider) {
      items.push({
        key: "provider",
        label: (
          <div style={{ cursor: "default" }}>
            <Tag color={provider.color} style={{ margin: 0 }}>
              {provider.name}
            </Tag>
          </div>
        ),
      });
    }

    items.push(
      { type: "divider" },
      {
        key: "logout",
        label: "Cerrar sesión",
        onClick: () => onLogout(),
      },
    );

    return items;
  }, [userProfile, userEmail, provider, onLogout]);

  function getHrefPath(path: string) {
    if (path.includes("complement")) {
      return `/cv${pathname}`;
    }
    return `/cv${path}`;
  }

  const breadcrumItems = useMemo(() => {
    const menuItem = findMenuItemByPath(menu, pathname);
    const distributionName =
      !pathname.includes("new") &&
      distributionPoint &&
      menuItem?.section === "Puntos de Distribución"
        ? {
            title: distributionPoint.name,
            href: undefined,
          }
        : undefined;

    const baseItems = [
      {
        href: "/cv",
        title: <HomeOutlined />,
      },
      {
        title: menuItem?.title,
        href: menuItem ? getHrefPath(menuItem.path) : undefined,
      },
    ];

    if (distributionName) {
      baseItems.splice(1, 0, distributionName);
    }
    return baseItems;
  }, [menu, pathname, distributionPoint]);

  return (
    <div className="header-title">
      <Breadcrumb items={breadcrumItems} />
      <TitlePage pathname={pathname} menu={menu} />
      <Dropdown
        menu={{ items: userDropdown }}
        trigger={["click"]}
        placement="bottomRight"
        className="settingSession"
      >
        <Avatar icon={<UserOutlined />} />
      </Dropdown>
    </div>
  );
}

export default HeaderBar;
